import {
  PropertyOperation,
  PropertyType,
  PropertySubtype,
  type CatalogPropertyTextSummary,
} from 'src/api/generated';
import type {
  CatalogPropertySummaryTranslate,
  CatalogPropertyTextViewModel,
} from 'src/modules/shared/types/catalog/property-summary.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { formatCurrency } from 'src/modules/shared/utils/formatting/currency.util';

const hasUsablePrice = (
  amount: string | null | undefined,
  currency: string | null | undefined
): amount is string =>
  amount !== null &&
  amount !== undefined &&
  amount.trim().length > 0 &&
  currency !== null &&
  currency !== undefined &&
  currency.trim().length > 0 &&
  Number.isFinite(Number(amount));

const formatPrice = (
  amount: string | null | undefined,
  currency: string | null | undefined
): string => {
  if (!hasUsablePrice(amount, currency) || !currency) {
    return '';
  }

  return formatCurrency(amount, currency, 0);
};

const propertyTypeTranslationKeys: Record<PropertyType, TranslationKey> = {
  [PropertyType.HOUSE]: 'catalog.properties.propertyType.HOUSE',
  [PropertyType.APARTMENT]: 'catalog.properties.propertyType.APARTMENT',
  [PropertyType.OFFICE]: 'catalog.properties.propertyType.OFFICE',
  [PropertyType.RETAIL]: 'catalog.properties.propertyType.RETAIL',
  [PropertyType.WAREHOUSE]: 'catalog.properties.propertyType.WAREHOUSE',
  [PropertyType.LAND]: 'catalog.properties.propertyType.LAND',
  [PropertyType.PARCEL]: 'catalog.properties.propertyType.PARCEL',
  [PropertyType.PARKING]: 'catalog.properties.propertyType.PARKING',
  [PropertyType.INDUSTRIAL]: 'catalog.properties.propertyType.INDUSTRIAL',
  [PropertyType.OTHER]: 'catalog.properties.propertyType.OTHER',
};

/** Builds the common type/locality and operation-price presentation without media or identifiers. */
export const buildCatalogPropertyTextViewModel = (
  property: CatalogPropertyTextSummary,
  t: CatalogPropertySummaryTranslate,
  separator = ' - '
): CatalogPropertyTextViewModel => {
  const propertyType = property.propertyType
    ? t(
        property.propertyType === PropertyType.APARTMENT &&
          property.propertySubtype === PropertySubtype.STUDIO
          ? 'catalog.properties.propertyType.APARTMENT_STUDIO'
          : propertyTypeTranslationKeys[property.propertyType]
      )
    : t('communications.conversationCard.property.fallbackType');
  const locality = property.locality?.trim();
  const displayTitle = locality
    ? `${propertyType}${separator}${locality}`
    : propertyType;
  const salePrice = formatPrice(
    property.salePriceAmount,
    property.salePriceCurrency
  );
  const rentPrice = formatPrice(
    property.rentPriceAmount,
    property.rentPriceCurrency
  );

  switch (property.operation) {
    case PropertyOperation.SALE:
      return {
        displayTitle,
        operation: t('catalog.properties.operation.SALE'),
        priceRows: [
          {
            label: t('catalog.properties.price.saleLabel'),
            accessibleLabel: t('catalog.properties.price.saleLabel'),
            value: salePrice,
          },
        ],
      };
    case PropertyOperation.RENT:
      return {
        displayTitle,
        operation: t('catalog.properties.operation.RENT'),
        priceRows: [
          {
            label: t('catalog.properties.price.rentLabel'),
            accessibleLabel: t('catalog.properties.price.rentLabel'),
            value: rentPrice,
          },
        ],
      };
    case PropertyOperation.TEMPORARY_RENT:
      return {
        displayTitle,
        operation: t('catalog.properties.operation.TEMPORARY_RENT'),
        priceRows: [
          {
            label: t(
              'communications.conversationCard.property.temporaryRentShort'
            ),
            accessibleLabel: t('catalog.properties.operation.TEMPORARY_RENT'),
            value: rentPrice,
          },
        ],
      };
    case PropertyOperation.SALE_AND_RENT:
      return {
        displayTitle,
        operation: t('catalog.properties.operation.SALE_AND_RENT'),
        priceRows: [
          {
            label: t('catalog.properties.price.saleLabel'),
            accessibleLabel: t('catalog.properties.price.saleLabel'),
            value: salePrice,
          },
          {
            label: t('catalog.properties.price.rentLabel'),
            accessibleLabel: t('catalog.properties.price.rentLabel'),
            value: rentPrice,
          },
        ],
      };
    case null:
    case undefined:
      return {
        displayTitle,
        operation: t(
          'communications.conversationCard.property.operationUnavailable'
        ),
        priceRows: [],
      };
  }
};
